/**
 * 学生看板（数据口径：我的课程 / 我的作业 / 我的课表）
 *
 * 布局：① 4 张 KPI ② 待交作业 + 本周课表 ③ 我的课程 + 个人档案/活动
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, List, Tag, Spin, Typography, Empty, message, Space, Descriptions } from 'antd';
import {
  BookOutlined, ExclamationCircleOutlined, CalendarOutlined, TrophyOutlined,
  ClockCircleOutlined, TeamOutlined,
} from '@ant-design/icons';
import { authApi } from '../../../services/api';
import { COURSE_STATUS_STYLE } from '../../../utils/courseConstants';
import type { StudentDashboardSummary } from '../../../services/types';

const { Text } = Typography;

const StudentOverview: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<StudentDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authApi.getStudentSummary()
      .then((res) => setData(res.data || null))
      .catch(() => message.error('加载看板数据失败'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }
  if (!data || data.message) {
    return <Empty description={data?.message || '看板数据加载失败，请刷新页面重试'} />;
  }

  const { kpi, pending_homework_list: pendingHomework, upcoming_sessions: upcomingSessions, my_courses: myCourses, activities, profile } = data;

  const kpiCards = [
    { title: '我的课程', value: kpi.my_courses, icon: <BookOutlined />, color: '#722ed1' },
    { title: '待交作业', value: kpi.pending_homework, icon: <ExclamationCircleOutlined />, color: kpi.pending_homework > 0 ? '#ff4d4f' : '#52c41a' },
    { title: '本周课次', value: kpi.this_week_sessions, icon: <CalendarOutlined />, color: '#1677ff' },
    { title: '参加活动', value: kpi.joined_activities, icon: <TrophyOutlined />, color: '#fa8c16' },
  ];

  return (
    <>
      {/* ① KPI */}
      <Row gutter={[16, 16]}>
        {kpiCards.map((card) => (
          <Col key={card.title} xs={12} md={6}>
            <Card style={{ height: '100%' }}>
              <Statistic title={card.title} value={card.value} prefix={card.icon} styles={{ content: { color: card.color, fontSize: 26 } }} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* ② 待交作业 + 本周课表 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card
            title="待交作业"
            size="small"
            style={{ height: '100%' }}
            extra={kpi.pending_homework > 0 ? <Tag color="red">{kpi.pending_homework} 项待完成</Tag> : <Tag color="green">全部已完成</Tag>}
          >
            {pendingHomework.length > 0 ? (
              <List
                size="small"
                dataSource={pendingHomework}
                renderItem={(hw) => (
                  <List.Item
                    style={{ padding: '8px 0', cursor: 'pointer' }}
                    onClick={() => navigate(`/my-courses/${hw.course_id}`)}
                    extra={hw.due_date
                      ? <Tag color={hw.is_overdue ? 'red' : 'blue'}>{hw.is_overdue ? '已逾期' : '截止'} {hw.due_date}</Tag>
                      : <Tag>无截止</Tag>}
                  >
                    <Space direction="vertical" size={0}>
                      <Space size={6} wrap>
                        <Text strong>{hw.title}</Text>
                        {hw.course_status === 'ended' && (
                          <Tag color="red" style={COURSE_STATUS_STYLE.ended.style}>已结束</Tag>
                        )}
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>{hw.course_name}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无待交作业" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title={<><ClockCircleOutlined style={{ marginRight: 6 }} />本周课表</>} size="small" style={{ height: '100%' }}>
            {upcomingSessions.length > 0 ? (
              <List
                size="small"
                dataSource={upcomingSessions}
                renderItem={(session) => (
                  <List.Item style={{ padding: '8px 0' }}>
                    <Space direction="vertical" size={0} style={{ width: '100%' }}>
                      <Text strong style={{ fontSize: 13 }}>{session.date} {session.start_time}{session.end_time ? `-${session.end_time}` : ''}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {session.course_name}{session.location ? ` · ${session.location}` : ''}
                      </Text>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="本周暂无课程安排" />
            )}
          </Card>
        </Col>
      </Row>

      {/* ③ 我的课程 + 个人档案 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card title={<><BookOutlined style={{ marginRight: 6 }} />我的课程（{myCourses.length}）</>} size="small" style={{ height: '100%' }}>
            {myCourses.length > 0 ? (
              <List
                dataSource={myCourses}
                renderItem={(course) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/my-courses/${course.id}`)}
                    extra={course.pending_homework_count > 0
                      ? <Tag color="red">{course.pending_homework_count} 项作业</Tag>
                      : <Tag color="green">作业已完成</Tag>}
                  >
                    <List.Item.Meta
                      title={(
                        <Space size={6} wrap>
                          <Text strong>{course.name}</Text>
                          {course.course_status === 'ended' && (
                            <Tag color="red" style={COURSE_STATUS_STYLE.ended.style}>已结束</Tag>
                          )}
                        </Space>
                      )}
                      description={[course.teacher_name, course.branch_name, course.semester_display].filter(Boolean).join(' · ')}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="还没有选课" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title={<><TeamOutlined style={{ marginRight: 6 }} />个人档案</>} size="small" style={{ height: '100%' }}>
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item label="所属学院">{profile.branch_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="年级班级">{profile.grade_display} {profile.class_name} 班</Descriptions.Item>
              <Descriptions.Item label="入院年份">{profile.admission_year || '—'}</Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 8 }}>
              <Tag color="blue">参加活动 {activities.total_joined} 场</Tag>
              <Tag color="green">进行中 {activities.ongoing}</Tag>
              <Tag>已结束 {activities.ended}</Tag>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default StudentOverview;
